import * as THREE from 'three'
import { markRaw } from 'vue'
import { usePortalStore } from '@/stores/portalStore.ts'
import type Portal from '@/types/Portal.ts'

export class MapPortalConnections {
    scene: THREE.Scene
    portalStore: any

    // Line segments for portal connections
    connectionLines: THREE.LineSegments | null = null

    // Material for connections
    material: THREE.Material | null = null

    // Performance tracking
    lastUpdateHash: string = ''

    constructor(scene: THREE.Scene) {
        this.scene = scene
        this.portalStore = usePortalStore()

        this.createMaterial()
        this.updateConnections()
    }

    createMaterial() {
        // Blue transparent material for wormhole connections
        this.material = markRaw(new THREE.LineBasicMaterial({
            color: 0x4488ff, // Blue
            transparent: true,
            opacity: 0.2,
            linewidth: 12 // 4x wider (note: not supported on all platforms, but works on some)
        }))
    }

    // Generate a hash of current portal connections for change detection
    generateConnectionsHash(): string {
        const portals = Array.from(this.portalStore.portals.values()) as Portal[]

        // Create a sorted list of connections to ensure consistent hash
        const connections = portals
            .filter(portal => portal.targetPortalId > 0)
            .map(portal => {
                // Sort portal IDs to avoid duplicate entries (A->B and B->A)
                const [id1, id2] = [portal.id, portal.targetPortalId].sort((a, b) => a - b)
                return `${id1}-${id2}`
            })
            .sort()
            .join('|')

        return btoa(connections).substring(0, 32)
    }

    updateConnections() {
        // Performance check: only update if portal connections have changed
        const currentHash = this.generateConnectionsHash()
        if (currentHash === this.lastUpdateHash) {
            return
        }
        this.lastUpdateHash = currentHash

        // Remove existing lines
        this.removeConnections()

        const portals = Array.from(this.portalStore.portals.values()) as Portal[]
        const points: THREE.Vector3[] = []

        // Track which connections we've already drawn to avoid duplicates
        const drawnConnections = new Set<string>()

        for (const portal of portals) {
            // Skip if no target
            if (!portal.targetPortalId || portal.targetPortalId === 0) {
                continue
            }

            // Find the target portal
            const targetPortal = this.portalStore.findPortalById(portal.targetPortalId)
            if (!targetPortal) {
                continue
            }

            // Create a unique key for this connection (sorted to avoid duplicates)
            const [id1, id2] = [portal.id, portal.targetPortalId].sort((a, b) => a - b)
            const connectionKey = `${id1}-${id2}`

            // Skip if we've already drawn this connection
            if (drawnConnections.has(connectionKey)) {
                continue
            }

            // Add this connection to our tracking set
            drawnConnections.add(connectionKey)

            // Add line segment points (z: -0.1 to render behind planets/ships)
            points.push(markRaw(new THREE.Vector3(portal.xPos, portal.yPos, -0.1)))
            points.push(markRaw(new THREE.Vector3(targetPortal.xPos, targetPortal.yPos, -0.1)))
        }

        // Create connection lines if we have points
        if (points.length > 0) {
            const geometry = markRaw(new THREE.BufferGeometry().setFromPoints(points))
            this.connectionLines = markRaw(new THREE.LineSegments(geometry, this.material!))
            this.connectionLines.name = 'portalConnections'
            this.scene.add(this.connectionLines)
        }
    }

    removeConnections() {
        if (this.connectionLines) {
            this.scene.remove(this.connectionLines)
            this.connectionLines.geometry.dispose()
            this.connectionLines = null
        }
    }

    setVisible(visible: boolean) {
        if (this.connectionLines) {
            this.connectionLines.visible = visible
        }
    }

    dispose() {
        this.removeConnections()

        if (this.material) {
            this.material.dispose()
            this.material = null
        }
    }

    // Called when portals are updated via WebSocket or other means
    onPortalsUpdated() {
        // Force update by clearing the hash
        this.lastUpdateHash = ''
        this.updateConnections()
    }
}
